import React, { useState } from 'react';
import { Stethoscope, Activity, Compass, ShieldCheck } from 'lucide-react';
import { AShareCTMatrixCard } from './AShareCTMatrixCard';
import { ASHARE_BILINGUAL } from '../../shared/constants/bilingualTitles';

export interface AShareCTMatrixViewProps {
  colorMode?: 'mainland' | 'international';
}

export const AShareCTMatrixView: React.FC<AShareCTMatrixViewProps> = ({
  colorMode = 'mainland',
}) => {
  const [sessionKind, setSessionKind] = useState<'DAY_CLOSE' | 'MIDDAY'>('DAY_CLOSE');

  return (
    <div className="view-container animate-fade-in" style={{ width: '100%', boxSizing: 'border-box' }}>
      {/* View Header */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <Stethoscope size={18} style={{ color: 'var(--accent-cyan)' }} />
            <h2 className="bilingual-header-title-zh">{ASHARE_BILINGUAL.ctMatrix.zh}</h2>
          </div>
          <span className="bilingual-header-subtitle-en">{ASHARE_BILINGUAL.ctMatrix.en}</span>
          <p className="view-subtitle" style={{ margin: '3px 0 0 0' }}>
            大盘呼吸度、全市场情绪量表、赚钱效应温度计与分板涨跌停全景透视
          </p>
        </div>

        {/* Session Switcher */}
        <div className="view-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>扫描周期:</span>
          <div style={{
            display: 'flex',
            borderRadius: '6px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-card)',
            padding: '2px',
          }}>
            <button
              onClick={() => setSessionKind('DAY_CLOSE')}
              style={{
                padding: '4px 12px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: sessionKind === 'DAY_CLOSE' ? 600 : 400,
                border: 'none',
                cursor: 'pointer',
                background: sessionKind === 'DAY_CLOSE' ? 'var(--accent-cyan)' : 'transparent',
                color: sessionKind === 'DAY_CLOSE' ? 'var(--text-inverse)' : 'var(--text-secondary)',
                transition: 'all 0.15s ease',
              }}
            >
              收盘全景 (DAY_CLOSE)
            </button>
            <button
              onClick={() => setSessionKind('MIDDAY')}
              style={{
                padding: '4px 12px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: sessionKind === 'MIDDAY' ? 600 : 400,
                border: 'none',
                cursor: 'pointer',
                background: sessionKind === 'MIDDAY' ? 'var(--accent-cyan)' : 'transparent',
                color: sessionKind === 'MIDDAY' ? 'var(--text-inverse)' : 'var(--text-secondary)',
                transition: 'all 0.15s ease',
              }}
            >
              午盘快照 (MIDDAY)
            </button>
          </div>
        </div>
      </div>

      {/* Main CT Panorama Dashboard Card */}
      <AShareCTMatrixCard colorMode={colorMode} sessionKind={sessionKind} />

      {/* Diagnostic & Methodology Section */}
      <div style={{
        marginTop: '24px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '16px',
      }}>
        {/* Method 1: Market Breadth */}
        <div style={{
          padding: '18px 20px',
          borderRadius: '8px',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-card)',
        }}>
          <div className="bilingual-card-title" style={{ marginBottom: '8px' }}>
            <h3 className="bilingual-card-title-zh" style={{ margin: 0, fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Activity size={16} className="icon-cyan" /> 大盘呼吸度模型
            </h3>
            <span className="bilingual-card-subtitle-en">MARKET BREADTH & VOLUME DELTA</span>
          </div>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            全景统计 5,500+ 只非停牌 A 股的上涨、下跌与平盘家数。结合全市场总成交额与量能增减幅（Volume Delta），以纯 Decimal 高精度计算全市场能量释放与多空力量对比。
          </p>
        </div>

        {/* Method 2: Sentiment Gauge */}
        <div style={{
          padding: '18px 20px',
          borderRadius: '8px',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-card)',
        }}>
          <div className="bilingual-card-title" style={{ marginBottom: '8px' }}>
            <h3 className="bilingual-card-title-zh" style={{ margin: 0, fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Compass size={16} className="icon-cyan" /> 赚钱效应情绪量表
            </h3>
            <span className="bilingual-card-subtitle-en">SENTIMENT COMPASS & MARKET PHASE</span>
          </div>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            采用 0–100 综合情绪打分机制，结合全市场均线多头占比（MA20/MA60 Ratio）及放量突破家数，精准划分市场所处周期（EXPANSION 扩张期 / CONTRACTION 收缩衰退期 / OVERHEATED 过热期 / COLD 冰点期）。
          </p>
        </div>

        {/* Method 3: Invariant Guarantee */}
        <div style={{
          padding: '18px 20px',
          borderRadius: '8px',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-card)',
        }}>
          <div className="bilingual-card-title" style={{ marginBottom: '8px' }}>
            <h3 className="bilingual-card-title-zh" style={{ margin: 0, fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <ShieldCheck size={16} className="icon-cyan" /> 金融级计算保证
            </h3>
            <span className="bilingual-card-subtitle-en">FINANCIAL-GRADE INVARIANTS</span>
          </div>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            100% 运行于只读计算网关，严格剥离订单与预算状态；满足闭市因果延迟窗口（W &ge; 3s）；全链路纯 Decimal 保证，零浮点舍入误差与零 Mock 假数据。
          </p>
        </div>
      </div>
    </div>
  );
};
